import { isNotNullish } from '@chainflip/utils/guard';
import type {} from '@redux-devtools/extension'; // required for devtools typing
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  DEFAULT_LPP_SLIPPAGE_PERCENT,
  DEFAULT_SLIPPAGE_PERCENT,
  DEFAULT_SWAP_DEADLINE_MINUTES,
} from '@/shared/constants';
import useSwapRequestStore from '@/shared/hooks/useSwapRequestStore';

type DepositMode = 'channel' | 'contract';
export type SlippageMode = 'auto' | 'custom';

interface SettingsData {
  boostEnabled: boolean;
  depositMode: DepositMode;
  slippageMode: SlippageMode;
  livePriceSlippageTolerancePercent: number;
  slippageTolerancePercent: number;
  swapDeadlineMinutes: number;
  isOnChainSwap: boolean;
  borrowTourStep: number | undefined;
  isBorrowTourComplete: boolean;
  shouldIntroduceLending: boolean;
  rewardsSeasonIndex: number | undefined;
  hideFundAccountModal: boolean;
}

const initialData: SettingsData = {
  boostEnabled: true,
  depositMode: 'channel',
  slippageMode: 'auto',
  livePriceSlippageTolerancePercent: DEFAULT_LPP_SLIPPAGE_PERCENT,
  slippageTolerancePercent: DEFAULT_SLIPPAGE_PERCENT,
  swapDeadlineMinutes: DEFAULT_SWAP_DEADLINE_MINUTES,
  isOnChainSwap: false,
  borrowTourStep: undefined,
  isBorrowTourComplete: false,
  shouldIntroduceLending: false,
  rewardsSeasonIndex: undefined,
  hideFundAccountModal: false,
} as const;

export type SettingsType = keyof SettingsData;

interface SettingsStore extends SettingsData {
  getSetting: <T extends SettingsType>(filter: T) => SettingsData[T];
  setSetting: <T extends SettingsType>(filter: T, value: SettingsData[T]) => void;
  toggleBoostEnabled: () => void;
  setBoostEnabled: (boostEnabled: boolean) => void;
  toggleSlippageMode: () => void;
  setIsOnChainSwap: (isOnChain: boolean) => void;
}

const useSettingsStore = create<SettingsStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...initialData,
        getSetting: (filter) => get()[filter],
        setSetting: (filter, value) => set({ [filter]: value }),
        toggleBoostEnabled: () =>
          set((state) => ({
            boostEnabled: !state.boostEnabled,
          })),
        setBoostEnabled: (boostEnabled) => set({ boostEnabled }),
        toggleSlippageMode: () =>
          set((state) => ({
            slippageMode: state.slippageMode === 'auto' ? 'custom' : 'auto',
          })),
        setIsOnChainSwap: (isOnChain) => set({ isOnChainSwap: isOnChain }),
      })),
      {
        name: 'cf.swap-settings',
        version: 10,
        migrate: (persistedState, version) => {
          const state = persistedState as SettingsStore;
          if (version < 5) {
            state.slippageTolerancePercent = DEFAULT_SLIPPAGE_PERCENT;
          }

          if (version < 6 && state.swapDeadlineMinutes === 15) {
            state.swapDeadlineMinutes = DEFAULT_SWAP_DEADLINE_MINUTES;
          }

          if (version < 7 && state.swapDeadlineMinutes === 5) {
            state.swapDeadlineMinutes = DEFAULT_SWAP_DEADLINE_MINUTES;
          }

          if (version < 8) {
            state.slippageMode = 'auto';
            state.livePriceSlippageTolerancePercent = DEFAULT_LPP_SLIPPAGE_PERCENT;
            state.slippageTolerancePercent = DEFAULT_SLIPPAGE_PERCENT;
            state.swapDeadlineMinutes = DEFAULT_SWAP_DEADLINE_MINUTES;
          }

          if (version < 9) {
            state.hideFundAccountModal = false;
          }

          if (version < 10) {
            state.isBorrowTourComplete = false;
            state.borrowTourStep = undefined;
            state.shouldIntroduceLending = true;
          } else {
            // Do not show lending 2.0 change introduction to new users
            state.shouldIntroduceLending = false;
          }

          return state;
        },
      },
    ),
  ),
);

export const getSlippageToleranceSubmission = () => {
  const { selectedRoute } = useSwapRequestStore();
  const {
    slippageMode,
    livePriceSlippageTolerancePercent,
    slippageTolerancePercent,
    swapDeadlineMinutes,
  } = useSettingsStore();

  const isLivePriceSlippageToleranceAvailable = isNotNullish(
    selectedRoute?.integration === 'chainflip' &&
      selectedRoute.integrationData?.quote?.recommendedLivePriceSlippageTolerancePercent,
  );

  if (slippageMode === 'auto') {
    return {
      mode: slippageMode,
      livePriceSlippageTolerancePercent:
        selectedRoute?.integration === 'chainflip'
          ? selectedRoute.integrationData?.quote?.recommendedLivePriceSlippageTolerancePercent
          : undefined,
      slippageTolerancePercent:
        (selectedRoute?.integration === 'chainflip'
          ? selectedRoute.integrationData?.quote?.recommendedSlippageTolerancePercent
          : undefined) ?? DEFAULT_SLIPPAGE_PERCENT,
      swapDeadlineMinutes:
        (selectedRoute?.integration === 'chainflip'
          ? selectedRoute.integrationData?.quote?.recommendedRetryDurationMinutes
          : undefined) ?? DEFAULT_SWAP_DEADLINE_MINUTES,
    };
  }

  return {
    mode: slippageMode,
    livePriceSlippageTolerancePercent: isLivePriceSlippageToleranceAvailable
      ? livePriceSlippageTolerancePercent
      : undefined,
    slippageTolerancePercent,
    swapDeadlineMinutes,
  };
};

export default useSettingsStore;
